import {
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui/base"
import {
	FieldArray,
	Form,
	GetFieldValue,
	arrayMutators,
	isTruthy,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"

type TSectionOrderingSettings = Array<{
	order: number
	title: string
	section: string
}>

type InitialValueType = {
	ordered_sections: TSectionOrderingSettings
	order_section_by_id: { [key: string]: boolean }
}

export function ReorderSections({
	sections,
	children,
	onSuccess,
}: {
	sections: TSectionOrderingSettings
	children: (props: { reOrder: () => void }) => React.ReactNode
	onSuccess: (value: TSectionOrderingSettings) => void
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children?.({ reOrder: open })}
			<Dialog open={isOpen} onClose={close} title="Re-Order">
				<Form<InitialValueType>
					onSubmit={withServerErrors(
						async ({ ordered_sections, order_section_by_id }) => {
							if (!ordered_sections || !order_section_by_id) close()
							const allOrderSection = ordered_sections.concat(
								sections.filter((s) => {
									const key = `section-${s.section}`
									return !isTruthy(order_section_by_id[key])
								})
							)
							close()
							onSuccess(allOrderSection)
						}
					)}
					mutators={{ ...arrayMutators }}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting, form }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<FieldArray<(typeof sections)[number]> name="ordered_sections">
									{({ fields }) => (
										<Inline gap="4" collapseBelow="sm">
											<Box flex="1">
												<Stack gap="6">
													<Stack gap="1">
														<Heading fontSize="md">
															Select the section in desired order
														</Heading>
														<Text fontSize="sm" color="muted">
															Section will be ordered in the order of clicks.
														</Text>
													</Stack>
													{sections.map((section, index) => (
														<Box key={index}>
															<GetFieldValue<boolean>
																name={`order_section_by_id.section-${section.section}`}
															>
																{({ value, onChange }) => {
																	const isSelected = isTruthy(value)
																	return (
																		<Inline
																			opacity={isSelected ? "20" : "100"}
																			gap="2"
																			alignItems={"center"}
																			transition="1"
																		>
																			<Box flex="1" minWidth="0">
																				<Box
																					key={`section-${section.section}`}
																					paddingX={"4"}
																					paddingY={"2"}
																					borderWidth="1"
																					rounded="md"
																				>
																					<Inline
																						gap="4"
																						alignItems={"center"}
																						justifyContent="between"
																					>
																						<Text>{section.title}</Text>
																						<Button
																							size="sm"
																							disabled={isSelected}
																							onClick={() => {
																								if (isSelected) return
																								onChange(true)
																								fields.push(section)
																							}}
																						>
																							<Icons.ArrowLeft rotate="180" />
																						</Button>
																					</Inline>
																				</Box>
																			</Box>
																		</Inline>
																	)
																}}
															</GetFieldValue>
														</Box>
													))}
												</Stack>
											</Box>
											<Box alignSelf={"stretch"} borderWidth="1"></Box>
											<Box flex="1">
												<Stack gap="6">
													<Stack gap="1">
														<Heading fontSize="md">Ordered Sections</Heading>
														<Text fontSize="sm" color="muted">
															To change the order, click on remove and then
															re-add
														</Text>
													</Stack>
													{fields.map((name, index) => {
														return (
															<Box key={name}>
																<Inline gap="2" alignItems={"center"}>
																	<Stack
																		size={"6"}
																		alignItems="center"
																		justifyContent="center"
																		borderWidth="1"
																		rounded="full"
																		bgColor="success_emphasis"
																		color="on_emphasis"
																	>
																		<Text fontWeight="semibold">
																			{index + 1}
																		</Text>
																	</Stack>
																	<Box flex="1" minWidth="0">
																		<GetFieldValue<(typeof sections)[0]>
																			name={name}
																		>
																			{({ value }) => (
																				<Box
																					key={``}
																					paddingX={"4"}
																					paddingY={"2"}
																					borderWidth="1"
																					rounded="md"
																				>
																					<Inline
																						gap="4"
																						alignItems={"center"}
																						justifyContent="between"
																					>
																						<Text>{value.title}</Text>
																						<Button
																							size="sm"
																							onClick={() => {
																								form.change(
																									`order_section_by_id.section-${value.section}` as never,
																									false as never
																								)
																								fields.remove(index)
																							}}
																						>
																							<Icons.Cancel />
																						</Button>
																					</Inline>
																				</Box>
																			)}
																		</GetFieldValue>
																	</Box>
																</Inline>
															</Box>
														)
													})}
												</Stack>
											</Box>
										</Inline>
									)}
								</FieldArray>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									Save Details
								</Button>
								<Button disabled={submitting} onClick={close}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}
